import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';

import type { ContractCockpitItem } from '../api/types';
import { ErrorMessage } from '../components/ErrorMessage';
import { MutationError } from '../components/MutationError';
import { fmtDateTime } from '../ops/format';
import { useProject } from '../projects/context';
import { projectKeys, useProjectApi } from '../projects/useProjectApi';

type Filter = 'all' | 'pending_approval' | 'approved' | 'sent' | 'signed';

const approvalLabels: Record<string, string> = {
  NONE: 'Entwurf',
  PENDING_APPROVAL: 'Wartet auf Freigabe',
  APPROVED: 'Freigegeben (GO)',
  REJECTED: 'Änderung gewünscht',
};

const signatureLabels: Record<string, string> = {
  NONE: 'Noch nicht versendet',
  PENDING: 'Gesendet (noch ungeöffnet)',
  VIEWED: 'Vom Eigentümer geöffnet',
  SIGNED: 'Unterschrieben zurück',
  DECLINED: 'Abgelehnt',
  EXPIRED: 'Link abgelaufen',
  CANCELED: 'Zurückgezogen',
};

export function ContractCockpitSection() {
  const { project, can } = useProject();
  const papi = useProjectApi();
  const queryClient = useQueryClient();
  const canApprove = can('project.manage');
  const [filter, setFilter] = useState<Filter>('all');

  const cockpit = useQuery({
    queryKey: projectKeys.contractCockpit(project.id),
    queryFn: ({ signal }) => papi.contractCockpit(signal),
    refetchInterval: 30_000,
  });

  const toggleMasterSwitch = useMutation({
    mutationFn: (next: boolean) => papi.setContractApprovalSetting(next),
    onSuccess: (data) => {
      queryClient.setQueryData(projectKeys.contractCockpit(project.id), data);
    },
  });

  const decide = useMutation({
    mutationFn: ({ docId, action, note }: { docId: string; action: 'SUBMIT' | 'APPROVE' | 'REJECT'; note?: string }) =>
      papi.updateDocumentApproval(docId, { action, note }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.contractCockpit(project.id) });
    },
  });

  const markPaper = useMutation({
    mutationFn: (docId: string) => papi.markDocumentSigned(docId, { note: 'Unterschrieben zurück (Cockpit)' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectKeys.contractCockpit(project.id) });
    },
  });

  if (cockpit.isError) return <ErrorMessage error={cockpit.error} onRetry={() => void cockpit.refetch()} />;
  const data = cockpit.data;
  if (!data) return null;

  const items = data.items.filter((it) => {
    switch (filter) {
      case 'pending_approval':
        return it.approval_status === 'PENDING_APPROVAL';
      case 'approved':
        return it.approval_status === 'APPROVED' && it.signature_status === 'NONE';
      case 'sent':
        return it.signature_status === 'PENDING' || it.signature_status === 'VIEWED';
      case 'signed':
        return it.signature_status === 'SIGNED';
      default:
        return true;
    }
  });

  const pendingCount = data.items.filter((it) => it.approval_status === 'PENDING_APPROVAL').length;
  const sentCount = data.items.filter((it) => it.signature_status === 'PENDING' || it.signature_status === 'VIEWED').length;
  const signedCount = data.items.filter((it) => it.signature_status === 'SIGNED').length;
  const base = `/p/${project.key}`;

  return (
    <section className="card contract-cockpit" aria-label="Auftrags- und Freigabe-Cockpit">
      <div className="section-head" style={{ flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2>Auftrags- &amp; Freigabe-Cockpit</h2>
          <p className="muted">
            Alle Maklerverträge im Projekt: Konditionen, Rechts-Schutzschild, Freigabe-Status und Live-Rücklauf-Überwachung (24-Std.-Takt Mo–Sa).
          </p>
        </div>
        {canApprove && (
          <label className="checkbox" style={{ fontWeight: 600 }}>
            <input
              type="checkbox"
              checked={data.require_contract_approval}
              disabled={toggleMasterSwitch.isPending}
              onChange={(e) => toggleMasterSwitch.mutate(e.target.checked)}
            />{' '}
            Freigabe-Pflicht vor Versand aktiv {data.require_contract_approval ? '(Jeder Vertrag braucht dein GO)' : '(Aus: Direktversand ohne Rückfrage)'}
          </label>
        )}
      </div>

      {(toggleMasterSwitch.error ?? decide.error ?? markPaper.error) && (
        <MutationError error={toggleMasterSwitch.error ?? decide.error ?? markPaper.error} />
      )}

      <div className="form-actions" style={{ margin: '10px 0', flexWrap: 'wrap' }}>
        <button type="button" className={filter === 'all' ? 'btn btn-small btn-primary' : 'btn btn-small'} onClick={() => setFilter('all')}>
          Alle ({data.items.length})
        </button>
        <button
          type="button"
          className={filter === 'pending_approval' ? 'btn btn-small btn-primary' : 'btn btn-small'}
          onClick={() => setFilter('pending_approval')}
        >
          Freigabe offen ({pendingCount})
        </button>
        <button type="button" className={filter === 'approved' ? 'btn btn-small btn-primary' : 'btn btn-small'} onClick={() => setFilter('approved')}>
          Freigegeben
        </button>
        <button type="button" className={filter === 'sent' ? 'btn btn-small btn-primary' : 'btn btn-small'} onClick={() => setFilter('sent')}>
          Unterwegs / 24h-Wächter ({sentCount})
        </button>
        <button type="button" className={filter === 'signed' ? 'btn btn-small btn-primary' : 'btn btn-small'} onClick={() => setFilter('signed')}>
          Unterschrieben ({signedCount})
        </button>
      </div>

      {items.length === 0 ? (
        <p className="muted">Keine Maklerverträge in dieser Ansicht.</p>
      ) : (
        <ul className="plain-list">
          {items.map((it) => (
            <CockpitRow
              key={it.document_id}
              item={it}
              base={base}
              canApprove={canApprove}
              busy={decide.isPending || markPaper.isPending}
              onApprove={() => decide.mutate({ docId: it.document_id, action: 'APPROVE' })}
              onSubmit={() => decide.mutate({ docId: it.document_id, action: 'SUBMIT' })}
              onReject={() => decide.mutate({ docId: it.document_id, action: 'REJECT', note: 'Bitte Konditionen im Konfigurator prüfen' })}
              onMarkPaper={() => markPaper.mutate(it.document_id)}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function CockpitRow({
  item: it,
  base,
  canApprove,
  busy,
  onApprove,
  onSubmit,
  onReject,
  onMarkPaper,
}: {
  item: ContractCockpitItem;
  base: string;
  canApprove: boolean;
  busy: boolean;
  onApprove: () => void;
  onSubmit: () => void;
  onReject: () => void;
  onMarkPaper: () => void;
}) {
  return (
    <li className="card" style={{ marginBottom: '8px', padding: '12px' }}>
      <div className="section-head" style={{ flexWrap: 'wrap', gap: '8px' }}>
        <div>
          <strong>
            <Link to={`${base}/dokumente/${it.document_id}`}>{it.title}</Link>
          </strong>{' '}
          · <Link to={`${base}/leads/${it.lead_id}`}>{it.lead_name}</Link>{' '}
          <span className="muted">
            ({[it.postal_code, it.city].filter(Boolean).join(' ')}
            {it.broker_name ? ` · Makler: ${it.broker_name}` : ''})
          </span>
        </div>
        <div className="form-actions">
          <span
            className={
              it.approval_status === 'APPROVED'
                ? 'badge badge-ok'
                : it.approval_status === 'PENDING_APPROVAL'
                  ? 'badge badge-warn'
                  : 'badge badge-muted'
            }
          >
            {approvalLabels[it.approval_status] ?? it.approval_status}
          </span>
          <span
            className={
              it.signature_status === 'SIGNED'
                ? 'badge badge-ok'
                : it.signature_status === 'VIEWED'
                  ? 'badge badge-info'
                  : it.signature_status === 'PENDING'
                    ? 'badge badge-warn'
                    : 'badge badge-muted'
            }
          >
            {signatureLabels[it.signature_status] ?? it.signature_status}
            {it.view_count > 0 ? ` (${it.view_count}×)` : ''}
          </span>
        </div>
      </div>

      <p className="muted" style={{ margin: '6px 0' }}>
        Objekt: <strong>{it.object_type || '–'}</strong> · Preis: <strong>{it.price ? `${it.price} €` : 'offen'}</strong> · Provision:{' '}
        <strong>
          Verkäufer {it.commission_seller || '0'} % / Käufer {it.commission_buyer || '0'} %
        </strong>{' '}
        ({it.commission_model || 'Standard'}) · Laufzeit: <strong>{it.term_months ? `${it.term_months} Monate` : 'unbefristet'}</strong>
      </p>

      {it.legal_warnings.length > 0 && (
        <p className="muted" style={{ margin: '4px 0' }}>
          <span className="badge badge-warn">{it.legal_overridden ? 'Rechtshinweis übersteuert' : 'Rechtshinweis'}</span>{' '}
          {it.legal_warnings.join(' ')}
        </p>
      )}

      {(it.first_viewed_at ?? it.next_followup_at ?? it.signed_at) && (
        <p className="muted" style={{ margin: '4px 0' }}>
          {it.first_viewed_at && `Erstmals geöffnet: ${fmtDateTime(it.first_viewed_at)} · `}
          {it.signed_at && `Unterschrieben: ${fmtDateTime(it.signed_at)} · `}
          {it.next_followup_at && it.signature_status !== 'SIGNED' && `Nächster Nachfass-Anruf (24h Mo–Sa): ${fmtDateTime(it.next_followup_at)}`}
        </p>
      )}

      <div className="form-actions" style={{ marginTop: '8px', flexWrap: 'wrap' }}>
        <Link className="btn btn-small" to={`${base}/dokumente/${it.document_id}`}>
          Im Konfigurator öffnen
        </Link>
        {it.approval_status !== 'APPROVED' && it.approval_status !== 'PENDING_APPROVAL' && (
          <button type="button" className="btn btn-small" disabled={busy} onClick={onSubmit}>
            Zur Freigabe vorlegen
          </button>
        )}
        {canApprove && it.approval_status !== 'APPROVED' && (
          <button type="button" className="btn btn-small btn-primary" disabled={busy} onClick={onApprove}>
            Freigeben (GO)
          </button>
        )}
        {canApprove && it.approval_status === 'PENDING_APPROVAL' && (
          <button type="button" className="btn btn-small btn-ghost" disabled={busy} onClick={onReject}>
            Änderung anfordern
          </button>
        )}
        {it.signature_status !== 'SIGNED' && (
          <button type="button" className="btn btn-small" disabled={busy} onClick={onMarkPaper}>
            Unterschrieben zurück (Papier/Scan)
          </button>
        )}
      </div>
    </li>
  );
}
